/**
 * @file CardSurfaceNotes.tsx
 * @description Подсказки под полями подложки секции (phase19.16, партия
 * SB120; замечание владельца 2026-10-19 «меняю тень и скругление подложки —
 * ничего не меняется», план пользователя п. 1):
 *
 * ```
 * ! Подложка (#FFFFFF) почти сливается с фоном страницы (#FDFBF7) — её видно
 *   только по тени. Цвет подложки — цвет «Карточки» сайта: Оформление → Цвета →
 *   Во всю ширину края подложки не видны — скругление и тень заметны только
 *   сверху и снизу. Сузьте блок («По сетке» или число), чтобы увидеть края.
 * ```
 *
 * Слияние считает `cardMergeOf` (lib/site-builder/look-card.ts). Ссылка на
 * цвет карточек — механизм «где это настраивается» (SB121, `useBuilderLink`
 * с группой «Фон и поверхности» вкладки «Цвета»). Подсказки, не запреты.
 */

import { DEFAULT_SITE_THEME, type SectionLook } from "@chaiberi/shared";
import { Link } from "react-router";

import { NoticeStrip } from "../../../ui/NoticeStrip";
import { FieldHint } from "../../../ui/settings-groups";
import { useSectionForm } from "../section-form-context";
import { useBuilderLink } from "../../../../hooks/site-builder/useBuilderLink";
import { cardMergeOf } from "../../../../lib/site-builder/look-card";

/** Ссылка на «Оформление → Цвета → Фон и поверхности» — там роль «Карточки» (SB121). */
function CardColorLink() {
  const link = useBuilderLink();
  return (
    <Link
      to={link({ panel: "appearance", sub: "colors", group: "surface" })}
      className="inline-flex min-h-12 items-center font-medium text-primary hover:underline"
    >
      Оформление → Цвета →
    </Link>
  );
}

export function CardSurfaceNotes({ look }: { look: SectionLook }) {
  const { theme } = useSectionForm();
  const merge = cardMergeOf(look, theme ?? DEFAULT_SITE_THEME);

  return (
    <>
      {merge ? (
        <NoticeStrip tone="warning">
          Подложка ({merge.card}) почти сливается с фоном страницы ({merge.page}) —{" "}
          {merge.shadow ? "её видно только по тени." : "без тени её не видно."}{" "}
          {merge.source === "cards" ? (
            <>
              Цвет подложки — цвет «Карточки» сайта: <CardColorLink />
            </>
          ) : (
            "Цвет подложки — фон блока: выберите другой в «Фоне»."
          )}
        </NoticeStrip>
      ) : null}
      {look.container === "full" ? (
        <FieldHint className="mt-0">
          Во всю ширину края подложки не видны — скругление и тень заметны только сверху и снизу.
          Сузьте блок («По сетке» или число), чтобы увидеть края.
        </FieldHint>
      ) : null}
    </>
  );
}
